/**
 * Canvas-based sub-hex map renderer.
 * Renders a hexagonal grid of sub-hexes within a parent hex.
 * Supports painting tiles and undo functionality.
 */

import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import type { SubHexData, SubHexViewport, SelectedTile, NeighborHexData, SubHexLoreEntry, PaintTool } from './types';
import { getTileUrl } from '@/components/HexMap/tiles';
import {
    offsetToPixel,
    getHexCorners,
    pixelToOffset,
    hexKey,
    loreHexKey,
    getHexesInRadiusOffset,
    getNeighborsOffset,
    getHexLineOffset,
    isWithinBoundaryOffset,
    HEX_SIZE,
    type AxialCoord,
} from '@/lib/hexMath';

interface Props {
    subHexes: Map<string, SubHexData>;
    neighbors: NeighborHexData[];
    mapRadius: number;
    viewport: SubHexViewport;
    width: number;
    height: number;
    selectedTile: SelectedTile | null;
    canEdit: boolean;
    isPainting: boolean;
    brushSize: number; // 1-5, number of hex rings to paint at once
    paintTool: PaintTool;
    lineStart: AxialCoord | null; // Start point for line tool
    loreEntries: Map<string, SubHexLoreEntry>; // Lore entries for golden glow (keyed by loreHexKey)
    currentLayerId: number | null; // Current layer ID for filtering lore display
    onHexClick?: (coord: AxialCoord) => void;
    onHexHover?: (coord: AxialCoord | null) => void;
    onPaintStart?: () => void;
    onPaintEnd?: (endCoord?: AxialCoord) => void; // End coordinate for line tool
    onPaintMove?: (coord: AxialCoord) => void;
    onLoreHover?: (lore: SubHexLoreEntry | null, screenX: number, screenY: number) => void;
    onLoreClick?: (coord: AxialCoord) => void;
    /** Called on mobile double-tap on lore hex (for navigation to library) */
    onLoreNavigate?: (coord: AxialCoord) => void;
}

// Colors for empty hexes and boundaries
const EMPTY_HEX_COLOR = 'rgba(30, 41, 59, 0.3)';
const EMPTY_HEX_STROKE = 'rgba(71, 85, 105, 0.5)';
const BOUNDARY_STROKE = 'rgba(139, 92, 246, 0.6)';
const HOVER_STROKE = 'rgba(255, 255, 255, 0.9)';
const ERASE_STROKE = 'rgba(239, 68, 68, 0.9)'; // Red for erase preview
const ERASE_FILL = 'rgba(239, 68, 68, 0.2)'; // Light red fill for erase preview
const SELECTED_PREVIEW_ALPHA = 0.6;

// Lore glow colors (golden shimmer)
const LORE_GLOW_BASE_ALPHA = 0.4;
const LORE_GLOW_PULSE_AMPLITUDE = 0.3;
const LORE_GLOW_COLOR = { r: 255, g: 215, b: 0 }; // Gold
const LORE_TOOL_HOVER_STROKE = 'rgba(255, 215, 0, 0.9)'; // Golden hover for lore tool

// How many rows of hexes to show from neighboring maps (fading out)
const NEIGHBOR_VISIBLE_ROWS = 5;

/**
 * Calculate the pixel offset for a neighboring hex map based on direction.
 * For flat-top hex tiling, the offset depends on the axial direction.
 * The circumradius of the entire sub-hex boundary is 2 * mapRadius * HEX_SIZE.
 */
function getNeighborPixelOffset(directionOffset: { q: number; r: number }, mapRadius: number, hexSize: number): { x: number; y: number } {
    // The circumradius of the entire sub-hex map boundary
    const circumradius = 2 * mapRadius * hexSize;

    // For flat-top hex tiling, use axial to pixel conversion scaled to the map circumradius
    // Standard flat-top hex: x = size * 3/2 * q, y = size * sqrt(3) * (r + q/2)
    const x = circumradius * 1.5 * directionOffset.q;
    const y = circumradius * Math.sqrt(3) * (directionOffset.r + directionOffset.q / 2);

    return { x, y };
}

export function SubHexMapCanvas({
    subHexes,
    neighbors,
    mapRadius,
    viewport,
    width,
    height,
    selectedTile,
    canEdit,
    isPainting,
    brushSize,
    paintTool,
    lineStart,
    loreEntries,
    currentLayerId,
    onHexClick,
    onHexHover,
    onPaintStart,
    onPaintEnd,
    onPaintMove,
    onLoreHover,
    onLoreClick,
    onLoreNavigate,
}: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

    // Hovered hex coordinate - using state so changes trigger re-render for brush preview
    const [hoveredHex, setHoveredHex] = useState<AxialCoord | null>(null);

    // Counter to force re-render when images load
    const [imagesLoaded, setImagesLoaded] = useState(0);

    // Animation phase for lore glow pulsing (0 to 2*PI)
    const [glowPhase, setGlowPhase] = useState(0);

    // Get or create a cached image, triggering re-render when it loads
    const getOrLoadImage = useCallback((url: string): HTMLImageElement | null => {
        let img = imageCacheRef.current.get(url);
        if (!img) {
            img = new Image();
            img.src = url;
            // Trigger re-render when image loads
            img.onload = () => setImagesLoaded(prev => prev + 1);
            imageCacheRef.current.set(url, img);
        }
        return img.complete ? img : null;
    }, []);

    // Animation loop for lore glow pulsing (only runs if there are lore entries)
    useEffect(() => {
        if (loreEntries.size === 0) return;

        let animationId: number;
        let lastTime = 0;
        const FPS = 20; // Target ~20 FPS for smooth pulsing
        const frameInterval = 1000 / FPS;

        const animate = (time: number) => {
            if (time - lastTime >= frameInterval) {
                lastTime = time;
                setGlowPhase(prev => (prev + 0.1) % (Math.PI * 2));
            }
            animationId = requestAnimationFrame(animate);
        };

        animationId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationId);
    }, [loreEntries.size]);

    // Draw lore glow effect on a hex
    const drawLoreGlow = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, phase: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            // Calculate pulsing intensity
            const intensity = LORE_GLOW_BASE_ALPHA + LORE_GLOW_PULSE_AMPLITUDE * Math.sin(phase);

            ctx.save();

            // Create hex path
            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Draw golden glow with shadow blur
            ctx.shadowColor = `rgba(${LORE_GLOW_COLOR.r}, ${LORE_GLOW_COLOR.g}, ${LORE_GLOW_COLOR.b}, ${intensity})`;
            ctx.shadowBlur = 10 * intensity * viewport.zoom;
            ctx.strokeStyle = `rgba(${LORE_GLOW_COLOR.r}, ${LORE_GLOW_COLOR.g}, ${LORE_GLOW_COLOR.b}, ${intensity})`;
            ctx.lineWidth = 3 * viewport.zoom;
            ctx.stroke();

            // Draw a second pass for more intense glow
            ctx.shadowBlur = 5 * intensity * viewport.zoom;
            ctx.lineWidth = 2 * viewport.zoom;
            ctx.stroke();

            ctx.restore();
        },
        [viewport.zoom]
    );

    // Draw lore tool hover effect (golden border)
    const drawLoreToolHover = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, hasLore: boolean) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Golden hover with icon indicator
            ctx.strokeStyle = LORE_TOOL_HOVER_STROKE;
            ctx.lineWidth = 2;
            ctx.stroke();

            // If hex has lore, show a subtle fill
            if (hasLore) {
                ctx.fillStyle = 'rgba(255, 215, 0, 0.15)';
                ctx.fill();
            }
        },
        [viewport.zoom]
    );

    // Draw a single hex with tile image
    const drawHex = useCallback(
        (ctx: CanvasRenderingContext2D, subHex: SubHexData, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Get tile URL and load image
            const url = subHex.tileImage ? getTileUrl(subHex.domain, subHex.tileImage) : null;

            if (url) {
                const img = getOrLoadImage(url);
                if (img) {
                    ctx.save();
                    ctx.clip();
                    const xs = corners.map(c => c.x);
                    const ys = corners.map(c => c.y);
                    const minX = Math.min(...xs);
                    const maxX = Math.max(...xs);
                    const minY = Math.min(...ys);
                    const maxY = Math.max(...ys);
                    ctx.drawImage(img, minX, minY, maxX - minX, maxY - minY);
                    ctx.restore();
                }
            }

            // Draw border
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.stroke();
        },
        [viewport.zoom, getOrLoadImage]
    );

    // Draw an empty hex
    const drawEmptyHex = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, isBoundary: boolean) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            ctx.fillStyle = EMPTY_HEX_COLOR;
            ctx.fill();
            ctx.strokeStyle = isBoundary ? BOUNDARY_STROKE : EMPTY_HEX_STROKE;
            ctx.lineWidth = isBoundary ? 2 : 0.5;
            ctx.stroke();
        },
        [viewport.zoom]
    );

    // Draw a neighbor hex with opacity (for fading effect)
    const drawNeighborHex = useCallback(
        (ctx: CanvasRenderingContext2D, subHex: SubHexData, centerX: number, centerY: number, opacity: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.save();
            ctx.globalAlpha = opacity;

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Get tile URL and load image
            const url = subHex.tileImage ? getTileUrl(subHex.domain, subHex.tileImage) : null;

            if (url) {
                const img = getOrLoadImage(url);
                if (img) {
                    ctx.save();
                    ctx.clip();
                    const xs = corners.map(c => c.x);
                    const ys = corners.map(c => c.y);
                    const minX = Math.min(...xs);
                    const maxX = Math.max(...xs);
                    const minY = Math.min(...ys);
                    const maxY = Math.max(...ys);
                    ctx.drawImage(img, minX, minY, maxX - minX, maxY - minY);
                    ctx.restore();
                }
            }

            // Draw subtle border
            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.lineWidth = 0.5;
            ctx.stroke();

            ctx.restore();
        },
        [viewport.zoom, getOrLoadImage]
    );

    // Draw hover effect with selected tile preview for a single hex
    const drawHexPreview = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, tile: SelectedTile | null) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // If we have a selected tile, show preview
            if (tile) {
                const img = getOrLoadImage(tile.url);
                if (img) {
                    ctx.save();
                    ctx.clip();
                    ctx.globalAlpha = SELECTED_PREVIEW_ALPHA;
                    const xs = corners.map(c => c.x);
                    const ys = corners.map(c => c.y);
                    const minX = Math.min(...xs);
                    const maxX = Math.max(...xs);
                    const minY = Math.min(...ys);
                    const maxY = Math.max(...ys);
                    ctx.drawImage(img, minX, minY, maxX - minX, maxY - minY);
                    ctx.globalAlpha = 1;
                    ctx.restore();
                }
            }

            // Draw hover border
            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();
            ctx.strokeStyle = HOVER_STROKE;
            ctx.lineWidth = 2;
            ctx.stroke();
        },
        [viewport.zoom, getOrLoadImage]
    );

    // Draw erase preview for a single hex (red outline)
    const drawErasePreview = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Fill with light red
            ctx.fillStyle = ERASE_FILL;
            ctx.fill();

            // Draw red border
            ctx.strokeStyle = ERASE_STROKE;
            ctx.lineWidth = 2;
            ctx.stroke();
        },
        [viewport.zoom]
    );

    // Draw erase preview for all hexes in the brush area (only those with tiles)
    const drawEraseBrushPreview = useCallback(
        (ctx: CanvasRenderingContext2D, centerCoord: AxialCoord, size: number) => {
            const scaledSize = HEX_SIZE * viewport.zoom;
            const mapCenterX = width / 2 + viewport.offsetX;
            const mapCenterY = height / 2 + viewport.offsetY;

            // Get all hexes in the brush radius using offset coordinates
            const hexesToPreview = getHexesInRadiusOffset(centerCoord.q, centerCoord.r, size);

            // Draw erase preview for each hex that's within the map boundary AND has a tile
            for (const hex of hexesToPreview) {
                if (!isWithinBoundaryOffset(hex.col, hex.row, mapRadius)) continue;

                // Only show erase preview for hexes that have tiles
                const key = hexKey({ q: hex.col, r: hex.row });
                if (!subHexes.has(key)) continue;

                const pixel = offsetToPixel(hex.col, hex.row, scaledSize);
                const screenX = mapCenterX + pixel.x;
                const screenY = mapCenterY + pixel.y;

                drawErasePreview(ctx, screenX, screenY);
            }
        },
        [viewport.zoom, viewport.offsetX, viewport.offsetY, width, height, mapRadius, subHexes, drawErasePreview]
    );

    // Draw brush preview for all hexes in the brush area
    const drawBrushPreview = useCallback(
        (ctx: CanvasRenderingContext2D, centerCoord: AxialCoord, tile: SelectedTile | null, size: number) => {
            const scaledSize = HEX_SIZE * viewport.zoom;
            const mapCenterX = width / 2 + viewport.offsetX;
            const mapCenterY = height / 2 + viewport.offsetY;

            // Get all hexes in the brush radius using offset coordinates
            const hexesToPreview = getHexesInRadiusOffset(centerCoord.q, centerCoord.r, size);

            // Draw preview for each hex that's within the map boundary
            for (const hex of hexesToPreview) {
                if (!isWithinBoundaryOffset(hex.col, hex.row, mapRadius)) continue;

                const pixel = offsetToPixel(hex.col, hex.row, scaledSize);
                const screenX = mapCenterX + pixel.x;
                const screenY = mapCenterY + pixel.y;

                drawHexPreview(ctx, screenX, screenY, tile);
            }
        },
        [viewport.zoom, viewport.offsetX, viewport.offsetY, width, height, mapRadius, drawHexPreview]
    );

    // Draw line preview from start to current hover position
    const drawLinePreview = useCallback(
        (ctx: CanvasRenderingContext2D, start: AxialCoord, end: AxialCoord, tile: SelectedTile | null) => {
            const scaledSize = HEX_SIZE * viewport.zoom;
            const mapCenterX = width / 2 + viewport.offsetX;
            const mapCenterY = height / 2 + viewport.offsetY;

            // Get all hexes along the line using offset coordinates
            const hexesToPreview = getHexLineOffset(start.q, start.r, end.q, end.r);

            // Draw preview for each hex that's within the map boundary
            for (const hex of hexesToPreview) {
                if (!isWithinBoundaryOffset(hex.col, hex.row, mapRadius)) continue;

                const pixel = offsetToPixel(hex.col, hex.row, scaledSize);
                const screenX = mapCenterX + pixel.x;
                const screenY = mapCenterY + pixel.y;

                drawHexPreview(ctx, screenX, screenY, tile);
            }
        },
        [viewport.zoom, viewport.offsetX, viewport.offsetY, width, height, mapRadius, drawHexPreview]
    );

    // Convert screen coordinates to hex coordinates
    const screenToHex = useCallback(
        (screenX: number, screenY: number): AxialCoord => {
            const centerX = width / 2 + viewport.offsetX;
            const centerY = height / 2 + viewport.offsetY;
            const scaledSize = HEX_SIZE * viewport.zoom;

            const pixelX = screenX - centerX;
            const pixelY = screenY - centerY;

            const { col, row } = pixelToOffset({ x: pixelX, y: pixelY }, scaledSize);
            return { q: col, r: row };
        },
        [width, height, viewport]
    );

    // Main render effect
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const animationId = requestAnimationFrame(() => {
            // Clear canvas with transparent background
            ctx.clearRect(0, 0, width, height);

            const centerX = width / 2 + viewport.offsetX;
            const centerY = height / 2 + viewport.offsetY;
            const scaledSize = HEX_SIZE * viewport.zoom;

            // The circumradius of the main hex map (used for calculating neighbor offsets)
            const mainMapCircumradius = 2 * mapRadius * scaledSize;

            // Draw neighboring hex sub-maps first (behind the main map)
            for (const neighbor of neighbors) {
                // Calculate the pixel offset for this neighbor's center
                const neighborOffset = getNeighborPixelOffset(neighbor.directionOffset, mapRadius, scaledSize);
                const neighborCenterX = centerX + neighborOffset.x;
                const neighborCenterY = centerY + neighborOffset.y;

                // Create a map for quick lookup of neighbor's sub-hexes
                const neighborSubHexMap = new Map<string, SubHexData>();
                for (const sh of neighbor.subHexes) {
                    neighborSubHexMap.set(hexKey({ q: sh.q, r: sh.r }), sh);
                }

                // Draw sub-hexes from this neighbor that are within NEIGHBOR_VISIBLE_ROWS of the main map
                const colExtent = Math.ceil(mapRadius * 4 / 3);
                for (let q = -colExtent; q <= colExtent; q++) {
                    for (let r = -mapRadius; r <= mapRadius; r++) {
                        if (!isWithinBoundaryOffset(q, r, mapRadius)) continue;

                        const key = hexKey({ q, r });
                        const neighborSubHex = neighborSubHexMap.get(key);
                        if (!neighborSubHex || !neighborSubHex.tileImage) continue;

                        // Calculate this sub-hex's screen position (in the neighbor's coordinate system)
                        const pixel = offsetToPixel(q, r, scaledSize);
                        const screenX = neighborCenterX + pixel.x;
                        const screenY = neighborCenterY + pixel.y;

                        // Check if hex is visible on screen
                        if (screenX < -scaledSize * 2 || screenX > width + scaledSize * 2) continue;
                        if (screenY < -scaledSize * 2 || screenY > height + scaledSize * 2) continue;

                        // Calculate distance from the main map center (in hex units)
                        // We want to show only hexes that are close to the main map boundary
                        const distFromMainCenter = Math.sqrt(
                            Math.pow(screenX - centerX, 2) + Math.pow(screenY - centerY, 2)
                        );

                        // The boundary of the main map is at mainMapCircumradius
                        // Show hexes that are within NEIGHBOR_VISIBLE_ROWS * scaledSize of the boundary
                        const maxDistance = mainMapCircumradius + NEIGHBOR_VISIBLE_ROWS * scaledSize * 1.5;
                        if (distFromMainCenter > maxDistance) continue;

                        // Calculate opacity based on distance (fade out as we get further from main map)
                        const distFromBoundary = distFromMainCenter - mainMapCircumradius;
                        const fadeRange = NEIGHBOR_VISIBLE_ROWS * scaledSize * 1.5;
                        const opacity = Math.max(0.1, 1 - (distFromBoundary / fadeRange) * 0.7);

                        drawNeighborHex(ctx, neighborSubHex, screenX, screenY, opacity);
                    }
                }
            }

            // Draw all hexes within the main boundary
            // For a regular flat-top hex, columns extend further than rows
            // Max column = circumradius / 1.5 = 2 * radius / 1.5 = 4/3 * radius
            const colExtent = Math.ceil(mapRadius * 4 / 3);
            for (let q = -colExtent; q <= colExtent; q++) {
                for (let r = -mapRadius; r <= mapRadius; r++) {
                    if (!isWithinBoundaryOffset(q, r, mapRadius)) continue;

                    const pixel = offsetToPixel(q, r, scaledSize);
                    const screenX = centerX + pixel.x;
                    const screenY = centerY + pixel.y;

                    // Check if hex is visible
                    if (screenX < -scaledSize * 2 || screenX > width + scaledSize * 2) continue;
                    if (screenY < -scaledSize * 2 || screenY > height + scaledSize * 2) continue;

                    const key = hexKey({ q, r });
                    const subHex = subHexes.get(key);

                    // Check if this is a boundary hex (any neighbor is outside)
                    const hexNeighbors = getNeighborsOffset(q, r);
                    const isBoundary = hexNeighbors.some(n => !isWithinBoundaryOffset(n.col, n.row, mapRadius));

                    if (subHex && subHex.tileImage) {
                        drawHex(ctx, subHex, screenX, screenY);
                    } else {
                        drawEmptyHex(ctx, screenX, screenY, isBoundary);
                    }
                }
            }

            // Draw lore glows on hexes that have lore entries (only for current layer)
            if (currentLayerId !== null) {
                for (const [key, lore] of loreEntries) {
                    // Only show lore glow for entries on the current layer/z-level
                    if (lore.layerId !== currentLayerId) continue;

                    const { q, r } = lore;
                    if (!isWithinBoundaryOffset(q, r, mapRadius)) continue;

                    const pixel = offsetToPixel(q, r, scaledSize);
                    const screenX = centerX + pixel.x;
                    const screenY = centerY + pixel.y;

                    // Check if hex is visible
                    if (screenX < -scaledSize * 2 || screenX > width + scaledSize * 2) continue;
                    if (screenY < -scaledSize * 2 || screenY > height + scaledSize * 2) continue;

                    drawLoreGlow(ctx, screenX, screenY, glowPhase);
                }
            }

            // Draw tool preview based on current tool
            if (hoveredHex && canEdit) {
                const { q, r } = hoveredHex;
                if (isWithinBoundaryOffset(q, r, mapRadius)) {
                    if (paintTool === 'lore') {
                        // Lore tool: show golden hover (check for lore on current layer only)
                        const hasLore = currentLayerId !== null && loreEntries.has(loreHexKey(currentLayerId, { q, r }));
                        const pixel = offsetToPixel(q, r, scaledSize);
                        const screenX = centerX + pixel.x;
                        const screenY = centerY + pixel.y;
                        drawLoreToolHover(ctx, screenX, screenY, hasLore);
                    } else if (paintTool === 'erase') {
                        // Erase tool: show red outline for hexes that will be erased
                        drawEraseBrushPreview(ctx, { q, r }, brushSize);
                    } else if (paintTool === 'line' && lineStart) {
                        // Line tool: show line from start to current hover
                        drawLinePreview(ctx, lineStart, { q, r }, selectedTile);
                    } else if (paintTool === 'brush') {
                        // Brush tool: show brush area preview
                        drawBrushPreview(ctx, { q, r }, selectedTile, brushSize);
                    } else if (paintTool === 'fill') {
                        // Fill tool: show single hex preview
                        drawBrushPreview(ctx, { q, r }, selectedTile, 1);
                    }
                }
            }
        });

        return () => cancelAnimationFrame(animationId);
    }, [subHexes, neighbors, mapRadius, viewport, width, height, selectedTile, canEdit, brushSize, paintTool, lineStart, hoveredHex, imagesLoaded, loreEntries, currentLayerId, glowPhase, drawHex, drawEmptyHex, drawNeighborHex, drawBrushPreview, drawLinePreview, drawEraseBrushPreview, drawLoreGlow, drawLoreToolHover]);

    // Mouse move handler - also handles continuous painting/erasing and lore hover
    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;

            const coord = screenToHex(e.clientX - rect.left, e.clientY - rect.top);
            if (isWithinBoundaryOffset(coord.q, coord.r, mapRadius)) {
                // Only update state if the hex actually changed (avoid unnecessary re-renders)
                setHoveredHex(prev => {
                    if (prev?.q === coord.q && prev?.r === coord.r) return prev;
                    return coord;
                });
                onHexHover?.(coord);

                // Check for lore hover on current layer only
                if (currentLayerId !== null) {
                    const key = loreHexKey(currentLayerId, coord);
                    const lore = loreEntries.get(key);
                    if (lore) {
                        // Screen coordinates relative to canvas container
                        const screenX = e.clientX - rect.left;
                        const screenY = e.clientY - rect.top;
                        onLoreHover?.(lore, screenX, screenY);
                    } else {
                        onLoreHover?.(null, 0, 0);
                    }
                } else {
                    onLoreHover?.(null, 0, 0);
                }

                // Continuous painting/erasing while mouse is held down
                // Erase tool doesn't require selectedTile, other tools do
                if (isPainting && canEdit && (paintTool === 'erase' || selectedTile)) {
                    onPaintMove?.(coord);
                }
            } else {
                setHoveredHex(null);
                onHexHover?.(null);
                onLoreHover?.(null, 0, 0);
            }
        },
        [screenToHex, mapRadius, onHexHover, isPainting, canEdit, selectedTile, paintTool, onPaintMove, loreEntries, currentLayerId, onLoreHover]
    );

    // Mouse leave handler
    const handleMouseLeave = useCallback(() => {
        setHoveredHex(null);
        onHexHover?.(null);
        onLoreHover?.(null, 0, 0);
        // End painting when mouse leaves canvas
        if (isPainting) {
            onPaintEnd?.();
        }
    }, [onHexHover, isPainting, onPaintEnd, onLoreHover]);

    // Track last tapped lore coord and time for mobile double-tap
    const lastLoreTapRef = useRef<{ coord: AxialCoord; time: number } | null>(null);
    // Track touch start position to distinguish taps from drags
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

    // Mouse down handler - start painting/erasing or lore click
    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (e.button !== 0) return;

            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;

            const coord = screenToHex(e.clientX - rect.left, e.clientY - rect.top);
            if (!isWithinBoundaryOffset(coord.q, coord.r, mapRadius)) return;

            // Check if this hex has lore (for view mode clicking)
            const hasLore = currentLayerId !== null && loreEntries.has(loreHexKey(currentLayerId, coord));

            // In edit mode with lore tool selected
            if (canEdit && paintTool === 'lore') {
                onLoreClick?.(coord);
                return;
            }

            // In view mode (not editing) - allow clicking on hexes with lore
            if (!canEdit && hasLore) {
                onLoreClick?.(coord);
                return;
            }

            // Other edit mode actions require canEdit
            if (!canEdit) return;

            // Erase tool doesn't require selectedTile, other tools do
            if (paintTool !== 'erase' && !selectedTile) return;

            onPaintStart?.();
            onHexClick?.(coord);
        },
        [canEdit, selectedTile, paintTool, screenToHex, mapRadius, onPaintStart, onHexClick, onLoreClick, currentLayerId, loreEntries]
    );

    // Mouse up handler - stop painting, pass current coordinate for line tool
    const handleMouseUp = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (isPainting) {
                // Get the current coordinate for line tool
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect) {
                    const coord = screenToHex(e.clientX - rect.left, e.clientY - rect.top);
                    if (isWithinBoundaryOffset(coord.q, coord.r, mapRadius)) {
                        onPaintEnd?.(coord);
                    } else {
                        onPaintEnd?.();
                    }
                } else {
                    onPaintEnd?.();
                }
            }
        },
        [isPainting, screenToHex, mapRadius, onPaintEnd]
    );

    // Touch start handler - track touch position for tap detection
    const handleTouchStart = useCallback(
        (e: React.TouchEvent<HTMLCanvasElement>) => {
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];
            touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
            };
        },
        []
    );

    // Touch end handler - detect taps on lore hexes (mobile)
    const handleTouchEnd = useCallback(
        (e: React.TouchEvent<HTMLCanvasElement>) => {
            if (!touchStartRef.current) return;

            const touchEnd = e.changedTouches[0];
            const dx = touchEnd.clientX - touchStartRef.current.x;
            const dy = touchEnd.clientY - touchStartRef.current.y;
            const duration = Date.now() - touchStartRef.current.time;

            // Check if this was a tap (not a drag): small movement and short duration
            const isTap = Math.abs(dx) < 10 && Math.abs(dy) < 10 && duration < 300;

            if (!isTap) {
                touchStartRef.current = null;
                return;
            }

            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) {
                touchStartRef.current = null;
                return;
            }

            const coord = screenToHex(touchEnd.clientX - rect.left, touchEnd.clientY - rect.top);
            if (!isWithinBoundaryOffset(coord.q, coord.r, mapRadius)) {
                touchStartRef.current = null;
                return;
            }

            // Check if this hex has lore
            const hasLore = currentLayerId !== null && loreEntries.has(loreHexKey(currentLayerId, coord));

            if (!hasLore) {
                touchStartRef.current = null;
                lastLoreTapRef.current = null;
                return;
            }

            // Check for double-tap (in view mode only)
            const now = Date.now();
            const lastTap = lastLoreTapRef.current;
            const isDoubleTap = lastTap &&
                lastTap.coord.q === coord.q &&
                lastTap.coord.r === coord.r &&
                (now - lastTap.time) < 400;

            if (!canEdit && isDoubleTap) {
                // Double-tap in view mode: navigate to library
                onLoreNavigate?.(coord);
                lastLoreTapRef.current = null;
            } else {
                // First tap: show lore modal
                onLoreClick?.(coord);
                lastLoreTapRef.current = { coord, time: now };
            }

            touchStartRef.current = null;
        },
        [screenToHex, mapRadius, currentLayerId, loreEntries, canEdit, onLoreClick, onLoreNavigate]
    );

    // Determine cursor style based on tool and lore availability
    const getCursorClass = () => {
        if (!canEdit) {
            // In view mode, show pointer if hovering over hex with lore
            if (hoveredHex && currentLayerId !== null && loreEntries.has(loreHexKey(currentLayerId, hoveredHex))) {
                return 'cursor-pointer';
            }
            return 'cursor-default';
        }
        if (paintTool === 'lore') return 'cursor-pointer';
        if (paintTool === 'erase' || selectedTile) return 'cursor-crosshair';
        return 'cursor-default';
    };

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className={getCursorClass()}
        />
    );
}

